import { useEffect } from 'react'
import { useAlerts as useAlertApi } from '~/api/app/alerts.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useLocalStorage } from '~/hooks/useLocalStorage'

export type AcknowledgeAlerts = {
  [key: string]: AcknowledgeAlert[]
}

export type AcknowledgeAlert = {
  time: number
  tagId?: string
  bidder?: string
  adUnit?: string
  acknowledge: boolean
}

const isAcknowledge = (alerts: AcknowledgeAlert[], rawAlert: AcknowledgeAlert): boolean => {
  const compare = (alert: AcknowledgeAlert, rawAlert: AcknowledgeAlert): boolean =>
    alert.time === rawAlert.time && alert.adUnit === rawAlert.adUnit && alert.tagId === rawAlert.tagId && alert.bidder === rawAlert.bidder
  const found = alerts.find((alert) => compare(alert, rawAlert))
  return !!found && found.acknowledge
}

export const useAlerts = () => {
  const user = useAuthenticatedUser()
  const { data } = useAlertApi()
  const [alerts, setAlerts] = useLocalStorage<AcknowledgeAlerts>({ key: 'acknowledgeAlerts', defaultValue: {} })

  useEffect(() => {
    if (!user) return
    if (!data) return

    setAlerts((alerts) => ({
      ...alerts,
      [user.currentScopeId]: data
        .map((value) => ({
          acknowledge: false,
          time: value.time,
          tagId: value.keys.tagId?.value,
          bidder: value.keys.bidder?.value,
          adUnit: value.keys.adUnit?.value,
        }))
        .map((value) => ({
          ...value,
          acknowledge: isAcknowledge(alerts[user.currentScopeId] || [], value),
        })),
    }))
  }, [data, user])

  return alerts
}

export const useAcknowledgeAlerts = () => {
  const user = useAuthenticatedUser()
  const [, setAlerts] = useLocalStorage<AcknowledgeAlerts>({ key: 'acknowledgeAlerts', defaultValue: {} })
  return () => {
    if (!user) return

    setAlerts((alerts) => ({
      ...alerts,
      [user.currentScopeId]: (alerts[user.currentScopeId] || []).map((alert) => ({ ...alert, acknowledge: true })),
    }))
  }
}
